/**
 * access.api.ts — Control de accesos (caseta). Espejo de `aws-serverless/src/api/access.handler.ts`.
 */
import { apiClient } from './client';

const base = '/api';

export type AccessDirection = 'IN' | 'OUT';
export type AccessSource = 'SCANNER' | 'CAMERA' | 'FACE' | 'MANUAL';

export interface GateView {
  sessionId: string;
  closedAt?: string;
  group?: string;
  destination?: string;
  notes?: string;
  present: number;
  names: string[];
  /** Lista completa con foto de enrolamiento (URL firmada de 15 min) y estatus. */
  people?: { employeeId: string; name: string; status: 'ACTIVE' | 'PRE_TERMINATION' | 'INACTIVE'; photoUrl?: string }[];
  extras?: { name: string }[];
}
export interface GateConfirmResult { result: 'MATCH' | 'MISMATCH'; expected: number; counted: number }

export type AccessResultCode = 'MATCH' | 'MISMATCH' | 'ALLOWED' | 'VISITOR' | 'DENIED' | 'PENDING' | 'AUTHORIZED';
export type TerminatedPolicy = 'STRICT' | 'VISITOR' | 'AUTHORIZE';

export interface AccessEvent {
  entryId: string;
  kind: 'PERSON_LIST' | 'PERSON' | 'VEHICLE';
  direction?: AccessDirection;
  source?: AccessSource;
  result?: AccessResultCode;
  at?: string;
  reason?: string;
  resolvedBy?: string;
  plate?: string;
  name?: string;
  similarity?: number;
  driver?: string;
  company?: string;
  occupants?: number;
  expected?: number;
  counted?: number;
  note?: string;
}
export interface VehicleRow { entryId: string; plate: string; driver?: string; company?: string; vehicleType?: string; authorized?: boolean; validUntil?: string }

export const fetchAccessConfig = async (): Promise<{ scanMode: 'BOTH' | 'SCANNER' | 'CAMERA'; terminatedPolicies: Record<AccessDirection, TerminatedPolicy>; canAuthorize: boolean }> =>
  (await apiClient.get(`${base}/access/config`)).data;
export const lookupGate = async (code: string): Promise<GateView> =>
  (await apiClient.get(`${base}/access/gate/${encodeURIComponent(code)}`)).data;
export const confirmGate = async (code: string, body: { counted: number; direction: AccessDirection; source: AccessSource; note?: string }): Promise<GateConfirmResult> =>
  (await apiClient.post(`${base}/access/gate/${encodeURIComponent(code)}/confirm`, body)).data;
export const fetchAccessEvents = async (date?: string): Promise<AccessEvent[]> =>
  (await apiClient.get(`${base}/access/events`, { params: date ? { date } : {} })).data.events;
export const fetchVehicles = async (): Promise<VehicleRow[]> =>
  (await apiClient.get(`${base}/access/vehicles`)).data.vehicles;
export const saveVehicle = async (body: { plate: string; driver?: string; company?: string; vehicleType?: string; validUntil?: string }) =>
  (await apiClient.post(`${base}/access/vehicles`, body)).data;
export const registerVehiclePass = async (body: { plate: string; direction: AccessDirection; driver?: string; company?: string; occupants?: number; note?: string }): Promise<{ result: 'ALLOWED' | 'VISITOR' }> =>
  (await apiClient.post(`${base}/access/vehicle-pass`, body)).data;

export interface GateRef { employeeId: string; name: string; status: 'ACTIVE' | 'PRE_TERMINATION' | 'INACTIVE' }
export type GateFace =
  | { index: number; kind: 'AUTO'; employee: GateRef; similarity: number }
  | { index: number; kind: 'NEEDS_CONFIRMATION'; candidates: (GateRef & { similarity: number })[] }
  | { index: number; kind: 'TERMINATED'; employee: GateRef; similarity: number }
  | { index: number; kind: 'UNKNOWN' | 'TOO_SMALL' };

export const identifyAtGate = async (imageBase64: string): Promise<GateFace[]> =>
  (await apiClient.post(`${base}/access/identify`, { imageBase64 })).data.results;
export const registerPersonPass = async (body: {
  direction: AccessDirection; source: AccessSource; employeeId?: string; method?: 'AUTO' | 'CONFIRMED';
  similarity?: number; visitorName?: string; company?: string; note?: string; reason?: string;
}): Promise<{ result: AccessResultCode; name: string }> =>
  (await apiClient.post(`${base}/access/person-pass`, body)).data;

/** Autoriza una solicitud pendiente de una persona dada de baja (permiso `attendance:gate_authorize`). */
export const authorizeAccessEntry = async (entryId: string, reason: string) =>
  (await apiClient.post(`${base}/access/events/${encodeURIComponent(entryId)}/authorize`, { reason })).data;
